import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const SECTION_BADGES: Record<'VCN' | 'HDR' | 'SANTE', { label: string; icon: string; className: string }> = {
  VCN: { label: 'VCN', icon: '🎓', className: 'bg-blue-100 text-blue-800' },
  HDR: { label: 'HDR', icon: '🏛️', className: 'bg-purple-100 text-purple-800' },
  SANTE: { label: 'SANTÉ', icon: '🩺', className: 'bg-emerald-100 text-emerald-800' },
};

export const AdminLayout: React.FC<{ children: React.ReactNode; activeSection?: string }> = ({ 
  children, 
  activeSection 
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const menuItems = [
    { path: '/admin', label: 'Dashboard', icon: '📊' },
    { path: '/admin/analytics', label: 'Analytics', icon: '📈' },
    { path: '/admin/engaged-members', label: 'Membres Engagés', icon: '⭐' },
    { path: '/admin/exposes', label: 'Exposés', icon: '📝' },
    { path: '/admin/ramadan', label: 'Ramadan', icon: '🌙' },
    { path: '/admin/events', label: 'Événements', icon: '📅' },
    { path: '/admin/galerie', label: 'Galerie', icon: '📸' },
    { path: '/admin/bibliotheque', label: 'Bibliothèque', icon: '📚' },
    { path: '/admin/mediatheque', label: 'Médiathèque', icon: '🎵' },
    { path: '/admin/daara', label: 'Daara', icon: '📖' },
    { path: '/admin/users', label: 'Utilisateurs', icon: '👥' },
    { path: '/admin/notifications', label: 'Notifications', icon: '📧' },
    { path: '/admin/advertisements', label: 'Annonces', icon: '📢' },
    { path: '/admin/contact', label: 'Messages Contact', icon: '📬' },
    { path: '/admin/knowledge-base', label: 'Base de Connaissances', icon: '💬' },
    { path: '/admin/chat-history', label: 'Historique Chat', icon: '💭' },
    { path: '/admin/public-questions', label: 'Questions Publiques', icon: '❓' },
  ];

  return (
    <div className="min-h-screen bg-green-50">
      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-0 h-screen bg-gradient-to-b from-green-700 to-green-900 text-white shadow-2xl z-50 transition-all duration-300 flex flex-col ${
          sidebarOpen ? 'w-64' : 'w-20'
        }`}
      >
        <div className="p-6 border-b border-white border-opacity-20 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              {sidebarOpen ? (
                <h1 className="text-2xl font-bold transition-all whitespace-nowrap text-white tracking-wide">
                  HANAFIA
                </h1>
              ) : (
                <div className="flex items-center justify-center w-8 h-8">
                  <span className="text-2xl font-bold">H</span>
                </div>
              )}
            </div>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors flex-shrink-0"
            >
              {sidebarOpen ? '◀' : '▶'}
            </button>
          </div>
        </div>
        
        <nav className="p-4 space-y-2 overflow-y-auto flex-1" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.3) rgba(255,255,255,0.1)' }}>
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                isActive(item.path)
                  ? 'bg-white bg-opacity-25 shadow-xl scale-105'
                  : 'hover:bg-white hover:bg-opacity-10 hover:translate-x-1'
              }`}
            >
              <span className="text-2xl min-w-[24px]">{item.icon}</span>
              <span className={`font-medium transition-all whitespace-nowrap ${sidebarOpen ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
                {item.label}
              </span>
            </Link>
          ))}
          
          <div className="pt-8 border-t border-white border-opacity-20">
            <Link
              to="/"
              className="flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-white hover:bg-opacity-10 hover:translate-x-1 transition-all duration-300"
            >
              <span className="text-2xl">🏠</span>
              <span className={`font-medium transition-all whitespace-nowrap ${sidebarOpen ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
                Site Public
              </span>
            </Link>
            
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-red-600 hover:bg-opacity-30 transition-all duration-300 mt-2"
            >
              <span className="text-2xl">🚪</span>
              <span className={`font-medium transition-all whitespace-nowrap ${sidebarOpen ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
                Déconnexion
              </span>
            </button>
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <main 
        className={`transition-all duration-300 p-8 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}
      >
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 animate-slide-down">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-green-700 to-green-900 bg-clip-text text-transparent">
                  Administration
                </h1>
                <p className="text-gray-600 mt-2 flex items-center space-x-2">
                  <span>Bienvenue,</span>
                  <span className="font-semibold text-green-700">{user?.first_name || user?.username}</span>
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="bg-gradient-to-r from-green-700 to-green-900 text-white rounded-full px-6 py-3 shadow-lg">
                  <span className="font-bold">
                    {user?.role === 'admin' ? '👑 Administrateur' : '👤 Membre'}
                  </span>
                </div>
                {user?.section && (
                  <div
                    className={`px-4 py-2 rounded-full font-semibold shadow-inner flex items-center gap-2 ${
                      SECTION_BADGES[(user.section as 'VCN' | 'HDR' | 'SANTE') || 'VCN'].className
                    }`}
                  >
                    <span>{SECTION_BADGES[(user.section as 'VCN' | 'HDR' | 'SANTE') || 'VCN'].icon}</span>
                    <span>{SECTION_BADGES[(user.section as 'VCN' | 'HDR' | 'SANTE') || 'VCN'].label}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="animate-fade-in">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
};
